import React, { useState } from 'react';
import {
  f0,
  fc,
  nextDueDate,
  moIncome,
  getLevelInfo,
  getWeatherForecast,
  calcPayoff,
  getAllCats,
  mk,
  getBeatLastMonth,
  getBillPriceChanges,
} from '../utils';
import { useDashboardData } from './dashboard/useDashboardData';
import DailyPanel from './DailyPanel';
import { SvgLineChart } from './SvgLineChart';
import { HealthScoreCard } from './dashboard/HealthScoreCard';
import { MilestonesCard } from './dashboard/MilestonesCard';
import { FinancialTipsCard } from './dashboard/FinancialTipsCard';
import { BillCalendarCard } from './dashboard/BillCalendarCard';
import { DailyAllowanceCard } from './dashboard/DailyAllowanceCard';
import { PaycheckPlanCard } from './dashboard/PaycheckPlanCard';
import { CashFlowForecastCard } from './dashboard/CashFlowForecastCard';
import { DailyPriorityCard } from './dashboard/DailyPriorityCard';
import { QuickEntryBar } from './dashboard/QuickEntryBar';
import { WeeklyRecapCard } from './dashboard/WeeklyRecapCard';
import { SetupWizardModal } from './setup/SetupWizardModal';
import { YearToDateCard } from './dashboard/YearToDateCard';
import { SpendingPredictionsCard } from './dashboard/SpendingPredictionsCard';
import { DebtPayoffCard } from './dashboard/DebtPayoffCard';
import { SpendingHeatmapCard } from './dashboard/SpendingHeatmapCard';
import { SubscriptionAuditCard } from './dashboard/SubscriptionAuditCard';
import XPStore from './XPStore';

const W = (k) => (w) => !w || w[k] !== false;

function SetupChecklist({ state, updateState, setTab, showWizard }) {
  if (state.checklistDismissed) return null;

  const inc  = Object.values(state.incomeLog || []).length > 0 || (state.monthlyIncome || 0) > 0;
  const bills = (state.fixedBills || []).length > 0 || (state.cards || []).length > 0;
  const spend = Object.values(state.varSpend || {}).some((m) => Object.values(m).some((e) => e.length > 0));
  const bank  = (state.bankBalance || 0) > 0;

  const steps = [
    { done: bills,  em: '📋', label: 'Add your bills & debts',  tab: 'plan:bills', hint: 'Plan → Bills' },
    { done: inc,    em: '💰', label: 'Log your income',          tab: 'plan:budget', hint: 'Plan → Budget' },
    { done: spend,  em: '🛒', label: 'Track some spending',       tab: 'spending', hint: 'Spending → List or Import' },
    { done: bank,   em: '🏦', label: 'Enter your bank balance',   tab: 'plan:bank', hint: 'Plan → Bank' },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null; // all done — hide silently

  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <div style={{ marginBottom: 14, padding: '16px 18px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 14, borderLeft: '3px solid var(--ylw)', position: 'relative' }}>
      <button
        onClick={() => updateState({ checklistDismissed: true })}
        style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 14 }}
        title="Dismiss"
      >✕</button>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800 }}>🚀 Get started — {doneCount}/{steps.length} done</div>
          <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 1 }}>Complete these steps for your first real snapshot</div>
        </div>
        <button
          onClick={showWizard}
          style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 9, border: 'none', background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' }}
        >
          ✨ Set up with AI
        </button>
      </div>
      <div style={{ height: 4, background: 'var(--bdr)', borderRadius: 99, overflow: 'hidden', marginBottom: 12 }}>
        <div style={{ height: '100%', width: pct + '%', background: 'var(--ylw)', borderRadius: 99, transition: 'width .5s' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {steps.map((s) => (
          <div
            key={s.label}
            onClick={() => !s.done && setTab?.(s.tab)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: s.done ? 'default' : 'pointer', opacity: s.done ? 0.5 : 1 }}
          >
            <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${s.done ? 'var(--grn)' : 'var(--bdr)'}`, background: s.done ? 'var(--grn)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: s.done ? '#000' : 'transparent', flexShrink: 0, fontWeight: 800 }}>
              ✓
            </div>
            <span style={{ fontSize: 12, fontWeight: s.done ? 400 : 600, textDecoration: s.done ? 'line-through' : 'none', color: s.done ? 'var(--mut)' : 'var(--txt)' }}>
              {s.em} {s.label}
            </span>
            {!s.done && <span style={{ fontSize: 10, color: 'var(--mut)', marginLeft: 'auto' }}>{s.hint} →</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionHeader({ label, open, onToggle, count }) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        width: '100%', padding: '9px 14px', marginBottom: open ? 10 : 12,
        background: 'var(--surf2)', border: '1px solid var(--bdr)', borderRadius: 10,
        cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!open && count > 0 && (
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--mut)', background: 'var(--bdr)', borderRadius: 99, padding: '2px 7px' }}>
            {count} items
          </span>
        )}
        <span style={{ fontSize: 11, color: 'var(--mut)', fontWeight: 600 }}>{open ? '▲' : '▼'}</span>
      </div>
    </button>
  );
}

export default function Dashboard({ state, updateState, showToast, setTab, features, setTheme, theme }) {
  const [dashCustomOpen, setDashCustomOpen] = useState(false);
  const [levelOpen, setLevelOpen] = useState(false);
  const [storeOpen, setStoreOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState('setup');
  const openWizard = (mode = 'setup') => { setWizardMode(mode); setWizardOpen(true); };
  const [questsOpen, setQuestsOpen] = useState(() => {
    try { const v = localStorage.getItem('questsOpen'); return v === null ? true : v !== 'false'; } catch { return true; }
  });
  const [sec, setSec] = useState(() => {
    try {
      const saved = localStorage.getItem('dash_sec');
      if (saved) return { today: true, month: true, bigpicture: false, ...JSON.parse(saved) };
    } catch {}
    return { today: true, month: true, bigpicture: false };
  });
  const toggleSec = (k) => setSec((s) => {
    const next = { ...s, [k]: !s[k] };
    try { localStorage.setItem('dash_sec', JSON.stringify(next)); } catch {}
    return next;
  });
  const dw = state.dashWidgets || {};

  const levelInfo = getLevelInfo(state);

  const {
    cats, now, k, pd, r,
    totBal, daily,
    inc, vari, savA, allBills, rem,
    mode, slider, varBudget, budgetRem, budgetColor, budgetPct, carryoverAmt,
    netWorth, nwColor, fNet,
    dueSoon, dueThisWeek, annualDueSoon,
    trends, hasTrends,
    budgetStreak, savingsStreak,
    savChartData, health,
    daysInMonth, dayOfMonth, forecast,
    milestones, nwHistory, insights, tips,
    cf,
    cashFlowForecast,
    lastMoKey, lastMoLabel, lastMoInc, lastMoPaid, lastMoVar, lastMoSav, lastMoRem, showRecap,
  } = useDashboardData(state);

  const {
    cards, fixedBills,
    catchUpSeen, recapSeen,
  } = state;
  const showCatchUp = !catchUpSeen && (fixedBills?.length > 0 || cards?.length > 0);
  const dismissCatchUp = () => updateState({ catchUpSeen: true });

  /* ── Widget counts per section (for collapsed label) ─────────────────────── */
  const TODAY_WIDGETS = ['paycheckPlan', 'dailyAllowance', 'billsDue'];
  const MONTH_WIDGETS = ['budget', 'cashFlow', 'streaks', 'recap'];
  const BIG_WIDGETS   = ['metrics', 'debtSnapshot', 'netWorth', 'savingsChart', 'health', 'spendingTrends', 'insights', 'milestones', 'tips', 'billCalendar'];
  const todayCount   = TODAY_WIDGETS.filter((k) => W(k)(dw)).length;
  const monthCount   = MONTH_WIDGETS.filter((k) => W(k)(dw)).length;
  const bigCount     = BIG_WIDGETS.filter((k) => W(k)(dw)).length;

  return (
    <div>
      {/* ── First-time catch-up modal ──────────────────────────────────────── */}
      {showCatchUp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 16, padding: 28, maxWidth: 440, width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,.4)' }}>
            <div style={{ fontSize: 32, marginBottom: 8, textAlign: 'center' }}>👋</div>
            <h2 style={{ fontWeight: 800, fontSize: 18, marginBottom: 6, textAlign: 'center' }}>One more thing before you dive in</h2>
            <p style={{ fontSize: 13, color: 'var(--mut)', marginBottom: 20, textAlign: 'center', lineHeight: 1.6 }}>
              Your dashboard is set up, but it doesn't know what's already happened this month. Take a few minutes to catch up:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              {[
                ['💰', 'Log income received so far', 'Go to Plan → Income & Budget and add any paychecks you\'ve already received this month.'],
                ['✅', 'Mark bills you\'ve already paid', 'In the Spending tab, tap each bill you\'ve already paid this month to mark it off.'],
                ['🛒', 'Add spending you\'ve already done', 'In the Spending tab, expand a spending category and log anything you\'ve already spent.'],
              ].map(([em, title, desc]) => (
                <div key={title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', background: 'var(--surf2)', borderRadius: 10, border: '1px solid var(--bdr)' }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{em}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{title}</div>
                    <div style={{ fontSize: 12, color: 'var(--mut)' }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={dismissCatchUp} style={{ width: '100%', padding: '12px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 14, border: 'none', cursor: 'pointer' }}>
              Got it, let's go →
            </button>
          </div>
        </div>
      )}

      {/* ── Setup Wizard modal ───────────────────────────────────────────── */}
      {wizardOpen && (
        <SetupWizardModal
          mode={wizardMode}
          onClose={() => setWizardOpen(false)}
          updateState={updateState}
          showToast={showToast}
          state={state}
        />
      )}

      {/* ── Get Started checklist ─────────────────────────────────────────── */}
      <SetupChecklist state={state} updateState={updateState} setTab={setTab} showWizard={() => openWizard('setup')} />

      {/* ── Persistent AI edit button ─────────────────────────────────────── */}
      {((state.fixedBills?.length > 0) || (state.subscriptions?.length > 0) || (state.cards?.length > 0)) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10, marginTop: -4 }}>
          <button
            onClick={() => openWizard('edit')}
            style={{
              padding: '6px 13px', borderRadius: 20,
              background: 'var(--surf)', border: '1px solid var(--bdr)',
              color: 'var(--txt)', fontWeight: 700, fontSize: 11,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            <span style={{ fontSize: 13 }}>✨</span> Edit setup with AI
          </button>
        </div>
      )}

      {/* ── One Thing To Do Today ─────────────────────────────────────────── */}
      <DailyPriorityCard
        setTab={setTab}
        dueSoon={dueSoon}
        budgetRem={budgetRem}
        budgetPct={budgetPct}
        inc={inc}
        vari={vari}
        varBudget={varBudget}
        savA={savA}
        savContrib={state.savContrib || 0}
        cards={state.cards}
        dayOfMonth={dayOfMonth}
        daysInMonth={daysInMonth}
        updateState={updateState}
        paidBills={state.paidBills}
        showToast={showToast}
      />

      {/* ── Quick Entry ───────────────────────────────────────────────────── */}
      <QuickEntryBar
        state={state}
        updateState={updateState}
        showToast={showToast}
        cats={cats}
        allBills={allBills}
        pd={pd}
      />

      {/* ── Weather + Level ────────────────────────────────────────────────── */}
      {(() => {
        const weather = getWeatherForecast({
          willGoNegative: state.bankBalance > 0 && (() => {
            const allB = (state.fixedBills || []).concat((state.cards || []).map((c) => ({ amt: c.min }))).concat((state.subscriptions || []).filter((s) => s.cycle !== 'annual'));
            return state.bankBalance > 0 && state.bankBalance < allB.reduce((s, b) => s + (b.amt || 0), 0) * 0.5;
          })(),
          isTight: state.bankBalance > 0 && state.bankBalance < 200,
          budgetRem, dueSoon, varBudget, vari, bankBalance: state.bankBalance || 0,
        });
        return (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'stretch' }}>
            <div style={{ flex: 1, padding: '10px 14px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderLeft: `3px solid ${weather.col}`, borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>{weather.em}</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: weather.col }}>{weather.label}</div>
                <div style={{ fontSize: 10, color: 'var(--mut)' }}>{weather.desc}</div>
              </div>
            </div>
            {features?.gamification !== false && (
              <button onClick={() => setLevelOpen(true)} style={{ padding: '10px 14px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 12, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 80, gap: 2 }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--ylw)', fontFamily: 'Syne,sans-serif' }}>Lv{levelInfo.level}</div>
                <div style={{ fontSize: 9, color: 'var(--mut)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px' }}>{levelInfo.name}</div>
                <div style={{ width: 48, height: 3, background: 'var(--bdr)', borderRadius: 99, overflow: 'hidden', marginTop: 2 }}>
                  <div style={{ height: '100%', width: levelInfo.pct + '%', background: 'var(--ylw)', borderRadius: 99 }} />
                </div>
              </button>
            )}
          </div>
        );
      })()}

      {/* ── Quick Stats ───────────────────────────────────────────────────── */}
      {(inc > 0 || varBudget > 0 || allBills.length > 0) && (() => {
        const paidCount = allBills.filter((b) => !!pd[b.id]).length;
        const savRate = inc > 0 ? Math.round((savA / inc) * 100) : null;
        const stats = [
          { em: '💰', val: inc > 0 ? '$' + (inc >= 1000 ? (inc / 1000).toFixed(1) + 'k' : Math.round(inc).toLocaleString()) : '—', label: 'Income' },
          { em: '💸', val: varBudget > 0 ? '$' + (Math.abs(budgetRem) >= 1000 ? (Math.abs(budgetRem) / 1000).toFixed(1) + 'k' : Math.round(Math.abs(budgetRem)).toLocaleString()) : '—', label: budgetRem < 0 ? 'Over budget' : 'Budget left', color: budgetRem < 0 ? 'var(--red)' : budgetRem < varBudget * 0.2 ? 'var(--ylw)' : 'var(--grn)' },
          { em: '✅', val: allBills.length > 0 ? `${paidCount}/${allBills.length}` : '—', label: 'Bills paid', color: paidCount === allBills.length && allBills.length > 0 ? 'var(--grn)' : paidCount === 0 && allBills.length > 0 ? 'var(--mut)' : 'var(--ylw)' },
          { em: '💹', val: savRate !== null ? savRate + '%' : savA > 0 ? '$' + (savA >= 1000 ? (savA / 1000).toFixed(1) + 'k' : Math.round(savA).toLocaleString()) : '—', label: 'Savings rate', color: savRate !== null ? (savRate >= 20 ? 'var(--grn)' : savRate >= 10 ? 'var(--ylw)' : savRate > 0 ? 'var(--txt)' : 'var(--mut)') : savA > 0 ? 'var(--grn)' : 'var(--mut)' },
        ];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 14 }}>
            {stats.map((s) => (
              <div key={s.label} style={{ padding: '10px 8px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 11, textAlign: 'center' }}>
                <div style={{ fontSize: 14, marginBottom: 2 }}>{s.em}</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: s.color || 'var(--txt)', lineHeight: 1.1, marginBottom: 2 }}>{s.val}</div>
                <div style={{ fontSize: 9, color: 'var(--mut)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.3px' }}>{s.label}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Level modal */}
      {levelOpen && features?.gamification !== false && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={(e) => e.target === e.currentTarget && setLevelOpen(false)}>
          <div style={{ background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 20, padding: 28, maxWidth: 360, width: '100%' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 48, fontWeight: 900, color: 'var(--ylw)', fontFamily: 'Syne,sans-serif', lineHeight: 1 }}>Lv{levelInfo.level}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)', marginTop: 4 }}>{levelInfo.name}</div>
              <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 2 }}>{levelInfo.xp.toLocaleString()} XP total</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--mut)', marginBottom: 4 }}>
                <span>{levelInfo.xpInLevel} / {levelInfo.xpNeeded} XP to next level</span>
                <span>{levelInfo.pct}%</span>
              </div>
              <div style={{ height: 8, background: 'var(--bdr)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: levelInfo.pct + '%', background: 'var(--ylw)', borderRadius: 99, transition: 'width 1s ease-out' }} />
              </div>
            </div>
            <div style={{ fontSize: 10, color: 'var(--mut)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>How to earn XP</div>
            {[
              { l: 'Month under budget', v: '+50 XP' },
              { l: 'Month with savings', v: '+30 XP' },
              { l: 'Historical tracking', v: '+40 XP/mo' },
              { l: 'Health score', v: 'up to +200 XP' },
              { l: 'Milestone earned', v: '+100 XP' },
              { l: 'Debt payoff progress', v: 'up to +300 XP' },
              { l: 'Savings goal reached', v: '+150 XP' },
            ].map((row) => (
              <div key={row.l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '5px 0', borderBottom: '1px solid var(--bdr)' }}>
                <span style={{ color: 'var(--mut)' }}>{row.l}</span>
                <span style={{ fontWeight: 700, color: 'var(--grn)' }}>{row.v}</span>
              </div>
            ))}
            {levelInfo.nextUnlock && (
              <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(255,217,61,.08)', border: '1px solid rgba(255,217,61,.25)', borderRadius: 10, fontSize: 12, color: 'var(--ylw)', fontWeight: 600 }}>
                {levelInfo.nextUnlock.em} Next unlock at Level {levelInfo.nextUnlock.level}: {levelInfo.nextUnlock.label}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={() => { setLevelOpen(false); setStoreOpen(true); }} style={{ flex: 1, padding: '10px', borderRadius: 10, background: 'var(--surf2)', color: 'var(--txt)', fontWeight: 800, fontSize: 13, border: '1px solid var(--bdr)', cursor: 'pointer' }}>
                🛍️ Visit Store
              </button>
              <button onClick={() => setLevelOpen(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 13, border: 'none', cursor: 'pointer' }}>
                Keep Grinding 💪
              </button>
            </div>
          </div>
        </div>
      )}

      {storeOpen && features?.gamification !== false && (
        <XPStore state={state} updateState={updateState} showToast={showToast} setTheme={setTheme} currentTheme={theme || 'dark'} onClose={() => setStoreOpen(false)} />
      )}

      {/* ── Daily Quests (collapsed by default) ───────────────────────────── */}
      {features?.gamification !== false && (
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => setQuestsOpen((o) => { const n = !o; try { localStorage.setItem('questsOpen', String(n)); } catch {} return n; })}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '8px 14px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--txt)' }}>⚡ Daily Quests</span>
            <span style={{ fontSize: 11, color: 'var(--mut)', fontWeight: 600 }}>{questsOpen ? '▲' : '▼'}</span>
          </button>
          {questsOpen && <div style={{ marginTop: 8 }}><DailyPanel state={state} updateState={updateState} showToast={showToast} /></div>}
        </div>
      )}

      {/* ── Customize panel ────────────────────────────────────────────────── */}
      {(() => {
        const SECTIONS = [
          {
            label: '📍 Right Now', widgets: [
              { k: 'dailyAllowance', l: 'Daily Allowance' },
              { k: 'billsDue', l: 'Bills Due Soon' },
            ],
          },
          {
            label: '📅 This Month', widgets: [
              { k: 'budget', l: 'Discretionary Budget' },
              { k: 'cashFlow', l: 'Cash Flow' },
              { k: 'streaks', l: 'Streaks' },
              { k: 'recap', l: 'Month Recap' },
            ],
          },
          {
            label: '📈 Big Picture', widgets: [
              { k: 'metrics', l: 'Key Metrics' },
              { k: 'debtSnapshot', l: 'Debt Overview' },
              { k: 'netWorth', l: 'Net Worth Chart' },
              { k: 'savingsChart', l: 'Savings Chart' },
              { k: 'health', l: 'Health Score' },
              { k: 'spendingTrends', l: 'Spending Trends' },
              { k: 'insights', l: 'Spending Insights' },
              { k: 'milestones', l: 'Milestones' },
              { k: 'tips', l: 'Financial Tips' },
              { k: 'billCalendar', l: 'Bill Calendar' },
            ],
          },
        ];
        return (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDashCustomOpen((o) => !o)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: dashCustomOpen ? 'var(--surf2)' : 'none', border: '1px solid var(--bdr)', borderRadius: 7, fontSize: 11, color: 'var(--mut)', cursor: 'pointer', fontWeight: 600 }}
              >
                ⚙️ Customize {dashCustomOpen ? '▲' : '▼'}
              </button>
            </div>
            {dashCustomOpen && (
              <div style={{ marginTop: 8, padding: '14px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 12 }}>
                  Show / Hide Widgets
                </div>
                {SECTIONS.map(({ label, widgets }) => (
                  <div key={label} style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--txt)', marginBottom: 6 }}>{label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {widgets.map(({ k, l }) => {
                        const on = dw[k] !== false;
                        return (
                          <button
                            key={k}
                            onClick={() => updateState({ dashWidgets: { ...dw, [k]: !on } })}
                            style={{ padding: '4px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid ' + (on ? 'var(--grn)' : 'var(--bdr)'), background: on ? 'rgba(107,203,119,.12)' : 'none', color: on ? 'var(--grn)' : 'var(--mut)', transition: 'all .15s' }}
                          >
                            {on ? '✓ ' : ''}{l}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ══════════════════════ RIGHT NOW ══════════════════════ */}
      {W('recap')(dw) && <WeeklyRecapCard state={state} updateState={updateState} varBudget={varBudget} />}

      <SectionHeader label="📍 Right Now" open={sec.today} onToggle={() => toggleSec('today')} count={todayCount} />
      {sec.today && (
        <>
          {/* Autopilot — default plan for the next paycheck */}
          {W('paycheckPlan')(dw) && <PaycheckPlanCard state={state} updateState={updateState} setTab={setTab} />}

          {/* Bank ↔ Budget bridge — only when bank balance is set */}
          {state.bankBalance > 0 && (() => {
            const paid = (state.paidBills || {})[`${now.getFullYear()}-${now.getMonth()}`] || {};
            const unpaidTotal = allBills.filter((b) => !paid[b.id]).reduce((s, b) => s + b.amt, 0);
            const afterBills = state.bankBalance - unpaidTotal;
            const varRemaining = Math.max(0, budgetRem); // budgetRem = how much discretionary is left
            const endOfMonth = afterBills - varRemaining;
            const col = endOfMonth < 0 ? 'var(--red)' : endOfMonth < 200 ? 'var(--ylw)' : 'var(--grn)';
            return (
              <div
                onClick={() => setTab?.('money')}
                style={{ marginBottom: 12, padding: '12px 14px', background: 'var(--surf)', border: `1px solid var(--bdr)`, borderLeft: `3px solid ${col}`, borderRadius: 12, cursor: 'pointer' }}
              >
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                  🏦 Bank → Month
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 2 }}>After bills</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: afterBills < 0 ? 'var(--red)' : afterBills < 300 ? 'var(--ylw)' : 'var(--txt)' }}>
                      {afterBills >= 0 ? f0(afterBills) : '−' + f0(Math.abs(afterBills))}
                    </div>
                    <div style={{ fontSize: 9, color: 'var(--mut)', marginTop: 1 }}>
                      {allBills.filter((b) => !paid[b.id]).length} bill{allBills.filter((b) => !paid[b.id]).length !== 1 ? 's' : ''} still due
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 2 }}>End of month est.</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: col }}>
                      {endOfMonth >= 0 ? f0(endOfMonth) : '−' + f0(Math.abs(endOfMonth))}
                    </div>
                    <div style={{ fontSize: 9, color: 'var(--mut)', marginTop: 1 }}>
                      after {f0(varRemaining)} more planned spending
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 8, textAlign: 'right' }}>
                  Full breakdown in Money → Bank →
                </div>
              </div>
            );
          })()}

          {/* Daily Allowance */}
          {W('dailyAllowance')(dw) && (
            <DailyAllowanceCard
              budgetRem={budgetRem}
              varBudget={varBudget}
              vari={vari}
              daysInMonth={daysInMonth}
              dayOfMonth={dayOfMonth}
              bankBalance={state.bankBalance || 0}
              bankBalanceDate={state.bankBalanceDate}
              bankCreditsTotal={(state.bankCredits || []).reduce((s, c) => s + (parseFloat(c.amt) || 0), 0)}
              unpaidBillsTotal={allBills.filter((b) => !pd[b.id]).reduce((s, b) => s + b.amt, 0)}
              unpaidBillsCount={allBills.filter((b) => !pd[b.id]).length}
              updateState={updateState}
            />
          )}

          {/* Spending anomaly callout — surfaces top over-trend category */}
          {dayOfMonth >= 10 && insights && (() => {
            const anomaly = insights.rows
              .filter((r) => r.pct !== null && r.pct >= 50 && r.cur > 20 && r.avg > 0)
              .sort((a, b) => b.pct - a.pct)[0];
            if (!anomaly) return null;
            const mult = (anomaly.cur / anomaly.avg).toFixed(1);
            return (
              <div
                onClick={() => setTab?.('spending')}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderLeft: '3px solid var(--ylw)', borderRadius: 11, marginBottom: 12, cursor: 'pointer' }}
              >
                <span style={{ fontSize: 20, flexShrink: 0 }}>{anomaly.em}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--txt)', marginBottom: 1 }}>
                    {anomaly.name} is running {mult}× higher than usual
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--mut)' }}>
                    {fc(anomaly.cur)} this month vs {fc(anomaly.avg)}/mo average — {anomaly.pct}% above normal
                  </div>
                </div>
                <span style={{ fontSize: 10, color: 'var(--mut)', flexShrink: 0 }}>→</span>
              </div>
            );
          })()}

          {/* Bills due this week */}
          {W('billsDue')(dw) && dueThisWeek.length > 0 && (
            <div className="card cp" style={{ marginBottom: 12, borderLeft: `3px solid ${dueSoon.length > 0 ? 'var(--ylw)' : 'var(--grn)'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: dueSoon.length > 0 ? 'var(--ylw)' : 'var(--grn)', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                  {dueSoon.length === 0 ? '✅ Bills This Week — All Paid' : '⏰ Bills Due This Week'}
                </div>
                <button onClick={() => setTab?.('spending:tracker')} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 20, border: '1px solid var(--bdr)', background: 'none', color: 'var(--mut)', cursor: 'pointer' }}>
                  Tracker →
                </button>
              </div>
              {dueThisWeek.map((b) => {
                const isPaid = !!pd[b.id];
                return (
                  <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 0', borderBottom: '1px solid var(--bdr)', opacity: isPaid ? 0.5 : 1 }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>{isPaid ? '✓' : '○'}</span>
                    <div className="dot" style={{ background: b.col }} />
                    <div style={{ flex: 1, fontSize: 12, fontWeight: 600, textDecoration: isPaid ? 'line-through' : 'none', color: isPaid ? 'var(--mut)' : 'var(--txt)' }}>{b.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--mut)' }}>{nextDueDate(b.due)}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: isPaid ? 'var(--mut)' : b.col }}>{fc(b.amt)}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Annual subscriptions due soon */}
          {annualDueSoon.length > 0 && (
            <div className="card cp" style={{ marginBottom: 12, borderLeft: '3px solid var(--pnk)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--pnk)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 8 }}>
                📅 Upcoming Annual Charges
              </div>
              {annualDueSoon.map((s) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 0', borderBottom: '1px solid var(--bdr)' }}>
                  <div className="dot" style={{ background: s.col || 'var(--pnk)' }} />
                  <div style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--mut)' }}>
                    {s.daysUntil === 0 ? 'Today' : `in ${s.daysUntil} day${s.daysUntil !== 1 ? 's' : ''}`}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--pnk)' }}>{fc(s.amt)}</div>
                </div>
              ))}
            </div>
          )}

          {/* Bill Price Changes */}
          {(() => {
            const changes = getBillPriceChanges(state);
            if (!changes.length) return null;
            return (
              <div className="card cp" style={{ marginBottom: 12, border: '1px solid rgba(255,107,107,.35)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 20 }}>🔔</span>
                  <div>
                    <div className="st" style={{ marginBottom: 0, color: 'var(--red)' }}>Bill Price Changes Detected</div>
                    <div style={{ fontSize: 10, color: 'var(--mut)' }}>These bills changed since last month</div>
                  </div>
                </div>
                {changes.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--bdr)' }}>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--mut)' }}>{f0(c.oldAmt)}</div>
                    <span style={{ fontSize: 10, color: 'var(--mut)' }}>→</span>
                    <div style={{ fontSize: 13, fontWeight: 700, color: c.delta > 0 ? 'var(--red)' : 'var(--grn)' }}>{f0(c.newAmt)}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: c.delta > 0 ? 'var(--red)' : 'var(--grn)' }}>
                      {c.delta > 0 ? '+' : ''}{f0(c.delta)}/mo
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Treat Yourself */}
          {(() => {
            if (!inc || varBudget <= 0 || dayOfMonth < 10) return null;
            const monthPct = dayOfMonth / daysInMonth;
            const spendPct = varBudget > 0 ? vari / varBudget : 1;
            if (spendPct >= monthPct - 0.15) return null;
            const treat = Math.round(Math.max(0, (varBudget * monthPct - vari) * 0.5));
            if (treat < 20) return null;
            return (
              <div style={{ marginBottom: 12, padding: '14px 18px', background: 'rgba(107,203,119,.08)', border: '1px solid rgba(107,203,119,.25)', borderRadius: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--grn)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>🎁 Treat Yourself</div>
                <div style={{ fontSize: 13, color: 'var(--txt)', lineHeight: 1.5 }}>
                  You've stayed <strong style={{ color: 'var(--grn)' }}>under budget</strong> {Math.round((1 - spendPct / monthPct) * 100)}% better than pace. You've earned a guilt-free <strong style={{ color: 'var(--grn)', fontSize: 16 }}>{fc(treat)}</strong> treat. 🎉
                </div>
                <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 4 }}>Keep the rest in your pocket.</div>
              </div>
            );
          })()}
        </>
      )}

      {/* ══════════════════════ THIS MONTH ══════════════════════ */}
      <SectionHeader label="📅 This Month" open={sec.month} onToggle={() => toggleSec('month')} count={monthCount} />
      {sec.month && (
        <>
          {/* Month-end recap (last 5 days of month) */}
          {(() => {
            const todayDate = now.getDate();
            const dim = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            if (todayDate < dim - 4) return null;
            const moLoggedInc = moIncome(state.incomeLog, k);
            const billsPaid = allBills.filter((b) => !!pd[b.id]).length;
            const onTrack = rem >= 0;
            const recapKey = `recap_${k}`;
            if (state.recapSeen?.[recapKey]) return null;
            return (
              <div className="card cp" style={{ marginBottom: 12, borderLeft: '3px solid var(--ylw)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ylw)', textTransform: 'uppercase', letterSpacing: '.5px' }}>🏁 Month-End Recap</div>
                  <button onClick={() => updateState({ recapSeen: { ...(state.recapSeen || {}), [recapKey]: true } })} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 15 }}>✕</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { l: '💰 Income logged', v: fc(moLoggedInc), c: 'var(--grn)' },
                    { l: '✅ Bills paid', v: `${billsPaid} / ${allBills.length}`, c: billsPaid === allBills.length ? 'var(--grn)' : 'var(--ylw)' },
                    { l: '🛒 Variable spending', v: fc(vari), c: vari > varBudget && varBudget > 0 ? 'var(--red)' : 'var(--txt)' },
                    { l: '💵 Savings deposited', v: fc(savA), c: 'var(--grn)' },
                    { l: onTrack ? '✅ Under budget' : '⚠️ Over budget', v: onTrack ? fc(rem) + ' left' : fc(Math.abs(rem)) + ' over', c: onTrack ? 'var(--grn)' : 'var(--red)' },
                  ].map((row) => (
                    <div key={row.l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--bdr)' }}>
                      <span style={{ color: 'var(--mut)' }}>{row.l}</span>
                      <span style={{ fontWeight: 700, color: row.c }}>{row.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Last month recap */}
          {W('recap')(dw) && showRecap && (
            <div className="card cp" style={{ marginBottom: 12, borderLeft: '3px solid var(--ylw)', position: 'relative' }}>
              <button
                onClick={() => {
                  const rs = { ...(recapSeen || {}), [lastMoKey]: true };
                  const keys = Object.keys(rs).sort();
                  if (keys.length > 24) keys.slice(0, keys.length - 24).forEach((k) => delete rs[k]);
                  updateState({ recapSeen: rs });
                }}
                style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 14 }}
              >✕</button>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ylw)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.4px' }}>
                📋 {lastMoLabel} Recap
              </div>
              <div className="bk">
                {[
                  { l: 'Earned', v: lastMoInc, c: 'var(--ylw)' },
                  { l: 'Bills', v: lastMoPaid, c: 'var(--red)' },
                  { l: 'Variable', v: lastMoVar, c: 'var(--pnk)' },
                  { l: 'Saved', v: lastMoSav, c: 'var(--grn)' },
                ].map((t) => (
                  <div key={t.l} className="bi2" style={{ borderLeftColor: t.c }}>
                    <div className="bil">{t.l}</div>
                    <div className="biv" style={{ color: t.c, fontSize: 14 }}>{f0(t.v)}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, marginTop: 6, color: lastMoRem >= 0 ? 'var(--grn)' : 'var(--red)' }}>
                {lastMoRem >= 0 ? `✓ ${fc(lastMoRem)} under budget` : `⚠️ ${fc(Math.abs(lastMoRem))} over budget`} in {lastMoLabel}
              </div>
            </div>
          )}

          {/* Discretionary budget */}
          {W('budget')(dw) && (inc > 0 && slider > 0 ? (
            <div className="card cp" style={{ marginBottom: 12, borderLeft: '3px solid ' + budgetColor }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                  {mode.emoji} {mode.name} · Discretionary Budget
                </span>
                <span style={{ fontSize: 11, color: 'var(--mut)' }}>{slider}% of free money</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 5 }}>
                <span style={{ fontSize: 12, color: 'var(--mut)' }}>Remaining</span>
                <span style={{ fontFamily: 'Syne,sans-serif', fontSize: 28, fontWeight: 800, color: budgetColor }}>
                  {budgetRem >= 0 ? fc(budgetRem) : '−' + fc(Math.abs(budgetRem))}
                </span>
              </div>
              <div className="pw"><div className="pb" style={{ background: budgetColor, width: budgetPct + '%' }}></div></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--mut)', marginTop: 3 }}>
                <span>{fc(vari)} spent</span><span>{fc(varBudget)} budget</span>
              </div>
              {forecast > 0 && dayOfMonth < daysInMonth && (
                <div style={{ fontSize: 11, color: forecast > varBudget && varBudget > 0 ? 'var(--red)' : 'var(--mut)', marginTop: 5 }}>
                  📈 On pace for {fc(forecast)} this month ({daysInMonth - dayOfMonth} days left)
                </div>
              )}
              {carryoverAmt > 0 && <div style={{ fontSize: 11, color: 'var(--grn)', marginTop: 5 }}>✓ Includes {fc(carryoverAmt)} rolled over from last month</div>}
              {budgetRem < 0 && <div className="wb">⚠️ You're {fc(Math.abs(budgetRem))} over your discretionary budget.</div>}
            </div>
          ) : inc > 0 && slider === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 10, marginBottom: 12, borderLeft: '3px solid var(--mut)' }}>
              <span style={{ fontSize: 16 }}>💰</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--mut)' }}>No Fun Budget — set a discretionary budget in 🗺️ Plan</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 10, marginBottom: 12, borderLeft: '3px solid ' + mode.color }}>
              <span style={{ fontSize: 16 }}>{mode.emoji}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: mode.color }}>{mode.name} · {mode.desc}</span>
              <span style={{ fontSize: 11, color: 'var(--mut)', marginLeft: 'auto' }}>Log paychecks to see your budget</span>
            </div>
          ))}

          {/* This Month live status */}
          {(allBills.length > 0 || inc > 0 || vari > 0) && (() => {
            const billsPaid = allBills.filter((b) => !!pd[b.id]).length;
            const billsTotal = allBills.length;
            const billsAllDone = billsPaid === billsTotal;
            const moLoggedInc = moIncome(state.incomeLog, k);
            const spendPct = varBudget > 0 ? Math.min(100, Math.round((vari / varBudget) * 100)) : 0;
            const todayDate = now.getDate();
            const dim = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            const moPct = Math.round((todayDate / dim) * 100);
            const spendAhead = varBudget > 0 && spendPct > moPct + 15;
            return (
              <div className="card cp" style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>📅 This Month</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13 }}>{billsAllDone ? '✅' : '📋'}</span>
                      <span style={{ fontSize: 12, color: 'var(--txt)' }}>
                        Bills — <strong style={{ color: billsAllDone ? 'var(--grn)' : 'var(--ylw)' }}>{billsPaid}/{billsTotal} paid</strong>
                      </span>
                    </div>
                    <button onClick={() => setTab?.('money')} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 20, border: '1px solid var(--bdr)', background: 'none', color: 'var(--mut)', cursor: 'pointer' }}>Go →</button>
                  </div>
                  {varBudget > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13 }}>{spendAhead ? '⚠️' : '🛒'}</span>
                        <span style={{ fontSize: 12, color: 'var(--txt)' }}>
                          Spending — <strong style={{ color: spendAhead ? 'var(--red)' : 'var(--grn)' }}>{fc(vari)} / {fc(varBudget)}</strong>
                          {spendAhead && <span style={{ fontSize: 10, color: 'var(--red)', marginLeft: 5 }}>ahead of pace</span>}
                        </span>
                      </div>
                      <button onClick={() => setTab?.('money')} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 20, border: '1px solid var(--bdr)', background: 'none', color: 'var(--mut)', cursor: 'pointer' }}>Go →</button>
                    </div>
                  )}
                  {moLoggedInc > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13 }}>💰</span>
                        <span style={{ fontSize: 12, color: 'var(--txt)' }}>Income — <strong style={{ color: 'var(--grn)' }}>{fc(moLoggedInc)} logged</strong></span>
                      </div>
                      <button onClick={() => setTab?.('money')} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 20, border: '1px solid var(--bdr)', background: 'none', color: 'var(--mut)', cursor: 'pointer' }}>Go →</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Spending Predictions */}
          <SpendingPredictionsCard
            insights={insights}
            dayOfMonth={dayOfMonth}
            daysInMonth={daysInMonth}
            vari={vari}
            varBudget={varBudget}
          />

          {/* Cash Flow */}
          {W('cashFlow')(dw) && (() => {
            if (cf.inc === 0) return null;
            const highAPR = (cards || []).filter((c) => c.bal > 0).sort((a, b) => b.apr - a.apr)[0];
            const nearGoal = (state.extraGoals || []).filter((g) => g.target > 0 && g.bal < g.target).sort((a, b) => b.bal / b.target - a.bal / a.target)[0];
            let tip = null;
            if (cf.leftOver > 0 && highAPR && highAPR.apr > 10)
              tip = { msg: `Put extra toward ${highAPR.name} (${highAPR.apr.toFixed(1)}% APR) to cut interest costs`, c: 'var(--grn)' };
            else if (cf.leftOver > 0 && nearGoal)
              tip = { msg: `${Math.round((nearGoal.bal / nearGoal.target) * 100)}% toward ${nearGoal.name} — ${f0(Math.max(0, nearGoal.target - nearGoal.bal))} to go`, c: 'var(--ylw)' };
            else if (cf.leftOver < 0)
              tip = { msg: `Planned spending exceeds income by ${f0(Math.abs(cf.leftOver))}`, c: 'var(--red)' };
            return (
              <div className="card cp" style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>📊 Monthly Cash Flow</div>
                {[
                  { l: '💰 Income', v: cf.inc, c: 'var(--grn)', bold: true },
                  { l: '📋 Fixed Bills', v: -cf.billsTotal, c: 'var(--red)' },
                  { l: '🛒 Variable Budget', v: -cf.varTotal, c: 'var(--pnk)' },
                  { l: '💵 Savings', v: -cf.savTotal, c: 'var(--blu)' },
                ].map((row) => (
                  <div key={row.l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--bdr)', fontSize: row.bold ? 14 : 13 }}>
                    <span style={{ fontWeight: row.bold ? 700 : 500 }}>{row.l}</span>
                    <span style={{ fontWeight: 700, color: row.c }}>{row.v >= 0 ? f0(row.v) : '−' + f0(Math.abs(row.v))}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: 16 }}>
                  <span style={{ fontWeight: 700 }}>Left Over</span>
                  <span style={{ fontWeight: 800, color: cf.leftOver >= 0 ? 'var(--grn)' : 'var(--red)' }}>
                    {cf.leftOver >= 0 ? f0(cf.leftOver) : '−' + f0(Math.abs(cf.leftOver))}
                  </span>
                </div>
                {tip && (
                  <div style={{ padding: '8px 10px', borderRadius: 8, fontSize: 11, color: tip.c, background: tip.c === 'var(--red)' ? 'rgba(255,107,107,.08)' : tip.c === 'var(--ylw)' ? 'rgba(255,217,61,.08)' : 'rgba(107,203,119,.08)', border: `1px solid ${tip.c === 'var(--red)' ? 'rgba(255,107,107,.2)' : tip.c === 'var(--ylw)' ? 'rgba(255,217,61,.2)' : 'rgba(107,203,119,.2)'}` }}>
                    {tip.c === 'var(--red)' ? '⚠️' : '💡'} {tip.msg}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Cash Flow Forecast — potential income vs. upcoming bills */}
          {W('cashFlowForecast')(dw) && <CashFlowForecastCard forecast={cashFlowForecast} />}

          {/* Streaks */}
          {W('streaks')(dw) && (budgetStreak > 0 || savingsStreak > 0) && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              {budgetStreak > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 99, fontSize: 11, fontWeight: 700 }}>
                  🔥 {budgetStreak}-month budget streak
                </div>
              )}
              {savingsStreak > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 99, fontSize: 11, fontWeight: 700 }}>
                  💰 Saving {savingsStreak} months straight
                </div>
              )}
            </div>
          )}

          {/* Beat Last Month */}
          {(() => {
            const rows = getBeatLastMonth(state);
            if (rows.length < 2) return null;
            const improved = rows.filter((r) => r.delta < 0).length;
            const worsened = rows.filter((r) => r.delta > 0).length;
            return (
              <div className="card cp" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                  <div className="st" style={{ marginBottom: 0 }}>⚡ Beat Last Month</div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: improved >= worsened ? 'var(--grn)' : 'var(--red)' }}>
                    {improved >= worsened ? `${improved} cats improved 🎉` : `${worsened} cats up ⚠️`}
                  </span>
                </div>
                <div style={{ fontSize: 9, color: 'var(--mut)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.5px' }}>This month vs last month</div>
                {rows.slice(0, 6).map((r) => (
                  <div key={r.cat} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{r.cat}</div>
                    <div style={{ fontSize: 11, color: 'var(--mut)', minWidth: 48, textAlign: 'right' }}>{f0(r.lastAmt)}</div>
                    <div style={{ fontSize: 10, color: 'var(--mut)' }}>→</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: r.delta < 0 ? 'var(--grn)' : r.delta > 0 ? 'var(--red)' : 'var(--mut)', minWidth: 48, textAlign: 'right' }}>{f0(r.thisAmt)}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: r.delta < 0 ? 'var(--grn)' : r.delta > 0 ? 'var(--red)' : 'var(--mut)', minWidth: 36, textAlign: 'right' }}>
                      {r.delta < 0 ? '▼' : r.delta > 0 ? '▲' : '–'}{r.pct !== null ? Math.abs(r.pct) + '%' : ''}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Weekly Digest */}
          {(() => {
            const moKey = mk(now.getFullYear(), now.getMonth());
            const weekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
            const allSpend = Object.values(state.varSpend?.[moKey] || {}).flat().filter((e) => e.date && new Date(e.date) >= weekAgo);
            const weekTotal = allSpend.reduce((s, e) => s + (e.amt || 0), 0);
            const topCat = Object.entries(state.varSpend?.[moKey] || {})
              .map(([cat, entries]) => ({ cat, total: entries.flat().filter((e) => e.date && new Date(e.date) >= weekAgo).reduce((s, e) => s + e.amt, 0) }))
              .filter((x) => x.total > 0).sort((a, b) => b.total - a.total)[0];
            if (weekTotal === 0) return null;
            return (
              <div className="card cp" style={{ marginBottom: 12, background: 'linear-gradient(135deg, var(--surf2), var(--surf))' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 22 }}>📰</span>
                  <div>
                    <div className="st" style={{ marginBottom: 0 }}>Your Week in Money</div>
                    <div style={{ fontSize: 10, color: 'var(--mut)' }}>{weekAgo.toLocaleDateString('default', { month: 'short', day: 'numeric' })} – today</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                  <div style={{ flex: 1, minWidth: 90, padding: '8px', background: 'var(--surf)', borderRadius: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--red)' }}>{f0(weekTotal)}</div>
                    <div style={{ fontSize: 10, color: 'var(--mut)' }}>spent this week</div>
                  </div>
                  {topCat && (
                    <div style={{ flex: 1, minWidth: 90, padding: '8px', background: 'var(--surf)', borderRadius: 10, textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--ylw)', textTransform: 'capitalize' }}>{topCat.cat}</div>
                      <div style={{ fontSize: 10, color: 'var(--mut)' }}>top category</div>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 90, padding: '8px', background: 'var(--surf)', borderRadius: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--blu)' }}>{allSpend.length}</div>
                    <div style={{ fontSize: 10, color: 'var(--mut)' }}>transactions</div>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--mut)', textAlign: 'center' }}>Every Sunday — check back next week!</div>
              </div>
            );
          })()}
        </>
      )}

      {/* ══════════════════════ BIG PICTURE ══════════════════════ */}
      <SectionHeader label="📈 Big Picture" open={sec.bigpicture} onToggle={() => toggleSec('bigpicture')} count={bigCount} />
      {sec.bigpicture && (
        <>
          {/* Key Metrics */}
          {W('metrics')(dw) && (
            <div className="g3">
              <div className="met" style={{ '--ac': 'var(--red)' }}>
                <div className="ml">Total Debt</div>
                <div className="mv" style={{ color: 'var(--red)' }}>{f0(totBal)}</div>
                <div className="ms">~{fc(daily)}/day interest</div>
              </div>
              <div className="met" style={{ '--ac': nwColor }}>
                <div className="ml">Net Worth</div>
                <div className="mv" style={{ color: nwColor }}>{fNet(netWorth)}</div>
                <div className="ms">savings minus debt</div>
              </div>
              <div className="met" style={{ '--ac': 'var(--grn)' }}>
                <div className="ml">Debt-Free In</div>
                <div className="mv" style={{ color: 'var(--grn)' }}>{r.mos ? r.mos + ' mo' : '—'}</div>
                <div className="ms">{r.end} · {f0(r.int)} interest</div>
              </div>
            </div>
          )}

          {/* Net Worth Sparkline */}
          {W('metrics')(dw) && nwHistory.length >= 3 && (() => {
            const mini = nwHistory.slice(-6).map((x) => ({ lbl: x.mo, nw: x.netWorth }));
            return (
              <div style={{ marginBottom: 12, padding: '10px 14px', background: 'var(--surf)', border: '1px solid var(--bdr)', borderRadius: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>
                  📈 Net Worth Trend
                </div>
                <SvgLineChart data={mini} series={[{ k: 'nw', c: netWorth >= 0 ? 'var(--grn)' : 'var(--red)', w: 2, fill: true }]} height={60} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--mut)', marginTop: 2 }}>
                  <span>{mini[0].lbl}</span>
                  <span style={{ fontWeight: 700, color: nwColor }}>{fNet(netWorth)} now</span>
                </div>
              </div>
            );
          })()}

          {/* Debt-Free Countdown */}
          {r.mos > 0 && totBal > 0 && (() => {
            const daysLeft = Math.round(r.mos * 30.44);
            const freeDate = (() => { const d = new Date(); d.setDate(d.getDate() + daysLeft); return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); })();
            return (
              <div style={{ marginBottom: 12, padding: '14px 18px', background: 'linear-gradient(135deg, rgba(107,203,119,.08), rgba(77,150,255,.06))', border: '1px solid rgba(107,203,119,.25)', borderRadius: 14, textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>🏁 Debt-Free Countdown</div>
                <div style={{ fontFamily: 'Syne,sans-serif', fontSize: 42, fontWeight: 900, color: 'var(--grn)', lineHeight: 1 }}>{daysLeft.toLocaleString()}</div>
                <div style={{ fontSize: 12, color: 'var(--mut)', marginTop: 4 }}>days · <strong style={{ color: 'var(--grn)' }}>{freeDate}</strong></div>
                <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 4, cursor: 'pointer' }} onClick={() => setTab?.('progress')}>📈 Progress → Debt to add extra payments →</div>
              </div>
            );
          })()}

          {/* Debt Overview */}
          {W('debtSnapshot')(dw) && cards && cards.length > 0 && (
            <div className="card cp" style={{ marginBottom: 12 }}>
              <div className="st">💳 Debt Overview</div>
              <div className="dcg">
                {cards.map((c) => {
                  const init = (state.initCards || []).find((x) => x.id === c.id) || c;
                  const pct2 = Math.max(0, Math.round(((init.bal - c.bal) / init.bal) * 100));
                  const eta = r.cpoff[c.id];
                  return (
                    <div key={c.id} className="dc">
                      <div style={{ height: 3, background: c.col, margin: '-16px -18px 10px' }}></div>
                      <div className="dcn"><div className="dot" style={{ background: c.col }}></div>{c.name}</div>
                      <div className="dcb" style={{ color: c.col }}>{f0(c.bal)}</div>
                      <div className="pw"><div className="pb" style={{ background: c.col, width: pct2 + '%' }}></div></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--mut)', marginTop: 2 }}>
                        <span>{pct2}% paid</span><span>{eta ? 'Free ' + eta.lbl : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Net Worth Chart */}
          {W('netWorth')(dw) && nwHistory.length >= 2 && (
            <div className="cc" style={{ marginBottom: 12 }}>
              <div className="st">📈 Net Worth Over Time</div>
              <SvgLineChart data={nwHistory} series={[{ k: 'netWorth', c: netWorth >= 0 ? 'var(--grn)' : 'var(--red)', w: 2.5, fill: true }]} height={160} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--mut)', marginTop: 4 }}>
                <span>{nwHistory[0].lbl}: {nwHistory[0].netWorth >= 0 ? f0(nwHistory[0].netWorth) : '−' + f0(Math.abs(nwHistory[0].netWorth))}</span>
                <span>Now: <strong style={{ color: nwColor }}>{fNet(netWorth)}</strong></span>
              </div>
            </div>
          )}

          {/* Savings Chart */}
          {W('savingsChart')(dw) && savChartData.length >= 2 && (
            <div className="cc" style={{ marginBottom: 12 }}>
              <div className="st">💵 Savings History</div>
              <SvgLineChart data={savChartData} series={[{ k: 'bal', c: 'var(--grn)', w: 2.5, fill: true }]} height={160} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--mut)', marginTop: 4 }}>
                <span>{savChartData[0].lbl}: {f0(savChartData[0].bal)}</span>
                <span>Now: {f0(savChartData[savChartData.length - 1].bal)}</span>
              </div>
            </div>
          )}

          {/* Health Score */}
          {W('health')(dw) && <HealthScoreCard health={health} />}

          {/* Spending Trends */}
          {W('spendingTrends')(dw) && hasTrends && (
            <div className="cc" style={{ marginBottom: 12 }}>
              <div className="st">📊 6-Month Variable Spending</div>
              <SvgLineChart data={trends} series={[{ k: 'total', c: 'var(--pnk)', w: 2.5, fill: true }]} height={160} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {cats.filter((cat) => trends.some((t) => t[cat.id] > 0)).map((cat) => {
                  const cur = trends[trends.length - 1][cat.id];
                  const prev = trends[trends.length - 2]?.[cat.id] || 0;
                  const delta = cur - prev;
                  return cur > 0 || prev > 0 ? (
                    <span key={cat.id} style={{ fontSize: 10, background: 'var(--surf2)', borderRadius: 99, padding: '2px 8px', color: cat.col, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {cat.em} {fc(cur)}
                      {prev > 0 && delta !== 0 && <span style={{ fontSize: 9, color: delta > 0 ? 'var(--red)' : 'var(--grn)', fontWeight: 700 }}>{delta > 0 ? '▲' : '▼'}{fc(Math.abs(delta))}</span>}
                    </span>
                  ) : null;
                })}
              </div>
              <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 4 }}>This month · colored arrows show change vs last month</div>
            </div>
          )}

          {/* Spending Insights */}
          {W('insights')(dw) && insights && insights.rows.length > 0 && (
            <div className="card cp" style={{ marginBottom: 12 }}>
              <div className="st">💡 Spending Insights</div>
              <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 12 }}>
                Averages based on the last {insights.monthsWithData} month{insights.monthsWithData !== 1 ? 's' : ''} vs. this month so far
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {insights.rows.map((row) => (
                  <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 16, width: 22, textAlign: 'center', flexShrink: 0 }}>{row.em}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{row.name}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {row.pct !== null && row.cur > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: row.pct > 15 ? 'var(--red)' : row.pct < -15 ? 'var(--grn)' : 'var(--mut)' }}>
                              {row.pct > 0 ? '▲' : '▼'}{Math.abs(row.pct)}%
                            </span>
                          )}
                          <span style={{ fontSize: 12, fontWeight: 700, color: row.col }}>{fc(row.cur)}</span>
                          <span style={{ fontSize: 10, color: 'var(--mut)' }}>/ {fc(row.avg)} avg</span>
                        </div>
                      </div>
                      <div style={{ background: 'var(--bdr)', borderRadius: 99, height: 4, overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 99, background: row.col, width: (row.avg > 0 ? Math.min(100, (row.cur / row.avg) * 100) : row.cur > 0 ? 100 : 0) + '%', transition: 'width .5s' }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {insights.rows.some((r) => r.pct !== null && r.pct > 20) && (
                <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 10, padding: '8px 10px', background: 'var(--surf2)', borderRadius: 8, border: '1px solid var(--bdr)' }}>
                  💡 {(() => { const top = insights.rows.filter((r) => r.pct > 20).sort((a, b) => b.pct - a.pct)[0]; return top ? `Your ${top.name} spending is ${top.pct}% above your usual average this month.` : ''; })()}
                </div>
              )}
            </div>
          )}

          {/* Subscription Audit */}
          {W('recurringCharges')(dw) && <SubscriptionAuditCard subscriptions={state.subscriptions} />}

          {/* Year to Date */}
          {W('ytd')(dw) && <YearToDateCard state={state} />}

          {/* Debt Payoff Simulator */}
          {W('payoffCompare')(dw) && <DebtPayoffCard state={state} r={r} totBal={totBal} />}

          {/* Spending Heatmap */}
          <SpendingHeatmapCard state={state} />

          {/* Spending DNA */}
          {(() => {
            const allCats = getAllCats(state.customCats, state.disabledCats);
            const months = [0, 1, 2].map((i) => { const d = new Date(now.getFullYear(), now.getMonth() - i, 1); return mk(d.getFullYear(), d.getMonth()); });
            const avgByCat = allCats.map((cat) => {
              const total = months.reduce((s, kk) => s + ((state.varSpend || {})[kk]?.[cat.id] || []).reduce((a, x) => a + x.amt, 0), 0);
              return { cat, avg: Math.round(total / months.length) };
            }).filter((x) => x.avg > 0).sort((a, b) => b.avg - a.avg).slice(0, 5);
            if (avgByCat.length < 2) return null;
            const max = avgByCat[0].avg;
            const topCat = avgByCat[0].cat;
            const personality = topCat.id === 'food' ? 'Foodie 🍔' : topCat.id === 'gas' ? 'Road Warrior ⛽' : topCat.id === 'shop' ? 'Shopper 🛍️' : topCat.id === 'ent' ? 'Entertainment Fan 🎮' : topCat.id === 'groc' ? 'Home Cook 🛒' : topCat.id === 'care' ? 'Self-Care Pro 💇' : 'Saver 💰';
            return (
              <div className="card cp" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                  <div className="st" style={{ marginBottom: 0 }}>🧬 Spending DNA</div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ylw)' }}>{personality}</span>
                </div>
                <div style={{ fontSize: 9, color: 'var(--mut)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.5px' }}>3-month average by category</div>
                {avgByCat.map(({ cat, avg }) => (
                  <div key={cat.id} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 13 }}>{cat.em}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{cat.name}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: cat.col || 'var(--ylw)' }}>{fc(avg)}/mo</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--bdr)', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: Math.round((avg / max) * 100) + '%', background: cat.col || 'var(--ylw)', borderRadius: 99, transition: 'width 0.8s ease-out' }} />
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Skip It Calculator */}
          {(() => {
            if (!totBal || !r.mos) return null;
            const allCats = getAllCats(state.customCats, state.disabledCats);
            const prevK = mk(now.getFullYear(), now.getMonth() - 1 < 0 ? 11 : now.getMonth() - 1);
            const prevVd = (state.varSpend || {})[prevK] || {};
            let topCat = null, topAmt = 0;
            allCats.forEach((cat) => {
              const budget = (state.catBudgets || {})[cat.id] || 0;
              const s = (prevVd[cat.id] || []).reduce((a, x) => a + x.amt, 0);
              if (s > 0 && s > topAmt && (budget === 0 || s > budget)) { topAmt = s; topCat = cat; }
            });
            if (!topCat || topAmt < 20) return null;
            const saving = Math.round(topAmt * 0.5);
            const rWith = calcPayoff(state.cards || [], (state.extra || 0) + saving, state.method || 'avalanche', state.lump || 0);
            const mosSaved = r.mos - rWith.mos;
            if (mosSaved < 1) return null;
            return (
              <div style={{ marginBottom: 12, padding: '14px 18px', background: 'rgba(255,107,107,.06)', border: '1px solid rgba(255,107,107,.2)', borderRadius: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>✂️ Skip It</div>
                <div style={{ fontSize: 13, color: 'var(--txt)', lineHeight: 1.6 }}>
                  Last month: <strong>{topCat.em} {topCat.name} — {fc(topAmt)}</strong>.<br />
                  Cut it by half → put <strong style={{ color: 'var(--grn)' }}>{fc(saving)}/mo</strong> toward debt →{' '}
                  <strong style={{ color: 'var(--grn)' }}>debt-free {mosSaved} month{mosSaved !== 1 ? 's' : ''} sooner</strong>.
                </div>
              </div>
            );
          })()}

          {/* Milestones */}
          {W('milestones')(dw) && <MilestonesCard milestones={milestones} />}

          {/* Financial Tips */}
          {W('tips')(dw) && <FinancialTipsCard tips={tips} setTab={setTab} />}

          {/* Bill Calendar */}
          {W('billCalendar')(dw) && allBills.length > 0 && (
            <BillCalendarCard allBills={allBills} paidToday={pd} now={now} />
          )}
        </>
      )}

      {/* ── Empty state ────────────────────────────────────────────────────── */}
      {(!cards || !cards.length) && (!fixedBills || !fixedBills.length) && !inc && (
        <div className="card cp" style={{ textAlign: 'center', padding: '32px 20px' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>💰</div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Your dashboard is empty</div>
          <div style={{ fontSize: 12, color: 'var(--mut)', lineHeight: 1.7 }}>
            Head to <strong>Settings</strong> to add your bills and income sources, then use <strong>Budget</strong> to log paychecks and <strong>Tracker</strong> to mark bills paid.
          </div>
        </div>
      )}
    </div>
  );
}
